import { useEffect, useState } from 'react';
import { useRender, useT } from '../context';
import { applyConsent, readConsent, saveConsent, type Consent } from '../tracking';

/**
 * Port of the legacy consent banner (_Layout.cshtml): same storage key, same Consent Mode v2 signals and the
 * same three choices. Rendered on the client only, so server HTML stays cacheable for everyone.
 */
export function CookieConsent() {
  const { mode } = useRender();
  const t = useT();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const [draft, setDraft] = useState<Consent>({ analytics: false, marketing: false });

  useEffect(() => {
    setMounted(true);
    const saved = readConsent();
    if (saved) {
      setDraft(saved);
      applyConsent(saved);
    } else {
      setOpen(true);
    }
  }, []);

  if (!mounted || mode === 'editor') return null;

  const choose = (consent: Consent) => {
    saveConsent(consent);
    applyConsent(consent);
    setDraft(consent);
    setOpen(false);
    setSettings(false);
  };

  return (
    <>
      {open ? (
        <div className="cookie-consent-banner kfz-visible" role="dialog" aria-live="polite" aria-label={t('cookieTitle')}>
          <h3>{t('cookieTitle')}</h3>
          <p>{t('cookieText')}</p>
          {settings ? (
            <div className="cookie-settings-panel kfz-visible">
              <label>
                <input type="checkbox" checked disabled /> {t('cookieNecessary')}
              </label>
              <label>
                <input type="checkbox" checked={draft.analytics} onChange={(e) => setDraft((d) => ({ ...d, analytics: e.target.checked }))} />{' '}
                {t('cookieAnalytics')}
              </label>
              <label>
                <input type="checkbox" checked={draft.marketing} onChange={(e) => setDraft((d) => ({ ...d, marketing: e.target.checked }))} />{' '}
                {t('cookieMarketing')}
              </label>
            </div>
          ) : null}
          <div className="cookie-consent-actions">
            <button type="button" className="cookie-primary" onClick={() => choose({ analytics: true, marketing: true })}>
              {t('cookieAcceptAll')}
            </button>
            <button type="button" onClick={() => choose({ analytics: false, marketing: false })}>
              {t('cookieRejectAll')}
            </button>
            <button type="button" aria-expanded={settings} onClick={() => setSettings((s) => !s)}>
              {t('cookieSettings')}
            </button>
            {settings ? (
              <button type="button" onClick={() => choose(draft)}>
                {t('cookieSave')}
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="cookie-settings-link cookie-floating-settings kfz-visible"
          onClick={() => {
            setDraft(readConsent() ?? { analytics: false, marketing: false });
            setSettings(true);
            setOpen(true);
          }}
        >
          {t('cookieTitle')}
        </button>
      )}
    </>
  );
}
